import React, { useState } from 'react';
import { Modal, Input, Typography, Button, Radio, Space, Divider, message, Switch, Form, Tooltip, Row, Col, Tabs } from 'antd';
import { CopyOutlined, CheckOutlined, InfoCircleOutlined, MobileOutlined, DesktopOutlined, EyeInvisibleOutlined } from '@ant-design/icons';

const { Text, Paragraph } = Typography;
const { TabPane } = Tabs;

const EmbedCodeModal = ({ visible, workflowId, onClose }) => {
  const [form] = Form.useForm();
  const [copied, setCopied] = useState(false);

  const [settings, setSettings] = useState({
    height: '500px',
    darkMode: false,
    showMinimap: true,
    showControls: true,
    nodesDraggable: false,
    nodesConnectable: false,
    elementsSelectable: false,
    showSidebar: false,
    allowPanZoom: true,
    showToolbar: false,
    allowDownload: false,
    hideOnMobile: false,
    hidePoweredBy: false
  });

  // Generate the shortcode based on current settings
  const generateShortcode = () => {
    let shortcode = `[wp_ai_workflow_preview id="${workflowId}"`;
    
    if (settings.height !== '500px') {
      shortcode += ` height="${settings.height}"`;
    }
    
    if (settings.darkMode) {
      shortcode += ` dark_mode="true"`;
    }
    
    if (!settings.showMinimap) {
      shortcode += ` show_minimap="false"`;
    }
    
    if (!settings.showControls) {
      shortcode += ` show_controls="false"`;
    }

    if (settings.nodesDraggable) {
      shortcode += ` nodes_draggable="true"`;
    }

    if (settings.nodesConnectable) {
      shortcode += ` nodes_connectable="true"`;
    }

    if (settings.elementsSelectable) {
      shortcode += ` elements_selectable="true"`;
    }

    if (settings.showSidebar) {
      shortcode += ` show_sidebar="true"`;
    }

    if (!settings.allowPanZoom) {
      shortcode += ` allow_pan_zoom="false"`;
    }

    if (settings.showToolbar) {
      shortcode += ` show_toolbar="true"`;
    }
    
    if (settings.allowDownload) {
      shortcode += ` allow_download="true"`;
    }
    
    // Add new options
    if (settings.hideOnMobile) {
      shortcode += ` hide_on_mobile="true"`;
    }
    
    if (settings.hidePoweredBy) {
      shortcode += ` hide_powered_by="true"`;
    }
    
    shortcode += `]`;
    return shortcode;
  };

  const copyToClipboard = () => {
    const shortcode = generateShortcode();
    navigator.clipboard.writeText(shortcode).then(() => {
      message.success('Shortcode copied to clipboard');
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    });
  };

  const handleValuesChange = (changedValues, allValues) => {
    // Auto-enable toolbar when download is enabled
    if (changedValues.allowDownload === true) {
      form.setFieldsValue({ showToolbar: true });
      allValues.showToolbar = true;
    }
    
    setSettings(allValues);
  };

  return (
    <Modal
      title="Embed Workflow Preview"
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
        <Button 
          key="copy" 
          type="primary" 
          icon={copied ? <CheckOutlined /> : <CopyOutlined />} 
          onClick={copyToClipboard}
        >
          {copied ? 'Copied!' : 'Copy Shortcode'}
        </Button>
      ]}
      width={700}
    >
      <Paragraph>
        Use this shortcode to embed an interactive preview of this workflow in any post or page.
      </Paragraph>
      
      <Input.TextArea
        value={generateShortcode()}
        readOnly
        autoSize={{ minRows: 2, maxRows: 3 }}
        style={{ marginTop: 8, marginBottom: 16, fontFamily: 'monospace' }}
      />
      
      <Form
        form={form}
        layout="vertical"
        initialValues={settings}
        onValuesChange={handleValuesChange}
      >
        <Tabs defaultActiveKey="basic" size="small" tabPosition="top" style={{ marginTop: -8 }}>
          <TabPane tab="Basic" key="basic">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item 
                  label="Height"
                  name="height"
                  tooltip="Controls the vertical size of the workflow viewer"
                >
                  <Radio.Group buttonStyle="solid">
                    <Radio.Button value="300px">300px</Radio.Button>
                    <Radio.Button value="500px">500px</Radio.Button>
                    <Radio.Button value="700px">700px</Radio.Button>
                  </Radio.Group>
                </Form.Item>
              </Col>
              
              <Col span={12}>
                <Form.Item 
                  label="Theme"
                  name="darkMode"
                  valuePropName="checked"
                  tooltip="Choose between light and dark mode"
                >
                  <Switch 
                    checkedChildren="Dark" 
                    unCheckedChildren="Light" 
                  />
                </Form.Item>
              </Col>
            </Row>
            
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item 
                  label="Hide on Mobile"
                  name="hideOnMobile"
                  valuePropName="checked"
                  tooltip="Hides the workflow on mobile devices (screens smaller than 768px)"
                >
                  <Switch 
                    checkedChildren={<MobileOutlined />} 
                    unCheckedChildren={<DesktopOutlined />} 
                  />
                </Form.Item>
              </Col>
              
              <Col span={12}>
                <Form.Item 
                  label="Powered By Text"
                  name="hidePoweredBy"
                  valuePropName="checked"
                  tooltip="Hide or show the 'Powered by AI Workflow Automation' text"
                >
                  <Switch 
                    checkedChildren="Hidden" 
                    unCheckedChildren="Visible" 
                  />
                </Form.Item>
              </Col>
            </Row>
          </TabPane>
          
          <TabPane tab="Interface" key="interface">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item 
                  label="Controls"
                  name="showControls"
                  valuePropName="checked"
                  tooltip="Displays navigation buttons (zoom in/out)"
                >
                  <Switch />
                </Form.Item>
                
                <Form.Item 
                  label="Minimap"
                  name="showMinimap"
                  valuePropName="checked"
                  tooltip="Shows a small overview map in the corner"
                >
                  <Switch />
                </Form.Item>
                
                <Form.Item 
                  label="Toolbar"
                  name="showToolbar"
                  valuePropName="checked"
                  tooltip="Displays a toolbar with additional features"
                >
                  <Switch />
                </Form.Item>
              </Col>
              
              <Col span={12}>
                <Form.Item 
                  label="Sidebar"
                  name="showSidebar"
                  valuePropName="checked"
                  tooltip="Shows a simplified sidebar with node types"
                >
                  <Switch />
                </Form.Item>
                
                <Form.Item 
                  label="Pan & Zoom"
                  name="allowPanZoom"
                  valuePropName="checked"
                  tooltip="Allows viewers to navigate the workflow"
                >
                  <Switch />
                </Form.Item>
                
                <Form.Item 
                  label="Download Button"
                  name="allowDownload"
                  valuePropName="checked"
                  tooltip="Adds a download button to export the workflow"
                >
                  <Switch />
                </Form.Item>
              </Col>
            </Row>
          </TabPane>
          
          <TabPane tab="Advanced" key="advanced">
            <Paragraph type="secondary" style={{ marginBottom: '12px' }}>
              These options allow viewers to interact with the workflow.
              Changes made by viewers won't affect the saved workflow.
            </Paragraph>
            
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item 
                  label="Draggable Nodes"
                  name="nodesDraggable"
                  valuePropName="checked"
                  tooltip="Allows viewers to reposition nodes"
                >
                  <Switch />
                </Form.Item>
              </Col>
              
              <Col span={8}>
                <Form.Item 
                  label="Connectable Nodes"
                  name="nodesConnectable"
                  valuePropName="checked"
                  tooltip="Allows creating new connections"
                >
                  <Switch />
                </Form.Item>
              </Col>
              
              <Col span={8}>
                <Form.Item 
                  label="Selectable Elements"
                  name="elementsSelectable"
                  valuePropName="checked"
                  tooltip="Allows selecting nodes and connections"
                >
                  <Switch />
                </Form.Item>
              </Col>
            </Row>
            
            <Paragraph type="secondary" style={{ fontSize: '12px', marginTop: '8px' }}>
              <InfoCircleOutlined style={{ marginRight: '6px' }} />
              These options are for demonstration purposes only.
            </Paragraph>
          </TabPane>
        </Tabs>
      </Form>
    </Modal>
  );
};

export default EmbedCodeModal;